import type { MarkingRecord } from '#shared/utils/markings/schema'

/**
 * The exact path a marking was made on, query and hash excluded — those point
 * at anchors within the same content.
 *
 * Deliberately NOT normalized: `markingsForPage` normalizes both sides when it
 * compares, so scoping here would buy nothing, while the workspace needs the
 * precise path to link back to the node the marking was made on. Normalizing
 * at capture sent those links to the top of the law instead.
 */
export function getCurrentPageUrl(): string {
  if (typeof window === 'undefined') {
    return ''
  }

  return window.location.pathname
}

export function normalizeMarkingPageUrl(path: string): string {
  if (!path) {
    return '/'
  }

  const withoutQueryAndHash = path.split('?')[0]?.split('#')[0] ?? path
  const collapsed = withoutQueryAndHash.replace(/\/{2,}/g, '/')

  // Law-reading URLs address sub-nodes (`/lov-…/§1/1.ledd`) of one document.
  // Scope markings to the law itself so a marking made on a deep link also
  // rehydrates on every other URL within the same law — locate simply skips
  // markings whose text isn't rendered on the current node.
  const lawScope = collapsed.match(/^(\/lover-og-lovkommentarer\/[^/]+)\//)

  if (lawScope?.[1]) {
    return lawScope[1]
  }

  if (collapsed.length > 1 && collapsed.endsWith('/')) {
    return collapsed.slice(0, -1)
  }

  return collapsed
}

// A law and its commentaries share one workspace card.
export function workKeyForMarking(marking: Pick<MarkingRecord, 'source'>): string {
  const source = marking.source

  if (source.contentType === 'law') {
    return `law:${source.workId ?? source.documentId}`
  }

  if (source.about?.lawId) {
    return `law:${source.about.lawId}`
  }

  return `${source.contentType}:${source.workId ?? source.documentId}`
}
